// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { speciesInfo, activityLabel, ACTIVITY_ORDER, OUTCOME_COLOUR } from '../domain';
import { speciesStats, speciesActivityMatrix } from '../analysis';
import { horizontalBars, heatColour, el } from '../components/charts';
import { squarify } from '../utils/squarify';
import { glossaryTerm } from '../glossary';
import { escapeHtml, formatNumber, formatPercent } from '../utils/format';

export function renderSpecies(root: HTMLElement, ctx: ViewContext): void {
  const stats = speciesStats(ctx.data.incidents);
  const named = stats.filter((s) => s.key !== 'unidentified');

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Which sharks, and how dangerous</h1>
      <p class="view-sub">
        Australia's incidents span more than a dozen species, but risk is wildly uneven. The
        ${glossaryTerm('big-three', 'three large species')} cause almost every death; most others leave only minor
        wounds. Click any shark for its profile.
      </p>
    </div>

    <div class="panel">
      <div class="panel-title">Incidents by species</div>
      <div class="panel-sub">Area = number of recorded incidents. Colour identifies the species. Click a tile to open it.</div>
      <div class="treemap-frame" data-role="treemap"></div>
    </div>

    <div class="grid-2" style="margin-top:var(--space-lg)">
      <div class="panel">
        <div class="panel-title">Fatal bites by species</div>
        <div class="panel-sub">Absolute deaths — concentrated in three species. Click a bar for detail.</div>
        <div class="chart-scroll" data-role="fatal"></div>
      </div>
      <div class="panel">
        <div class="panel-title">Fatal rate by species</div>
        <div class="panel-sub">Share of that species' incidents that proved fatal (species with 10+ incidents).</div>
        <div class="chart-scroll" data-role="rate"></div>
      </div>
    </div>

    <div class="panel" style="margin-top:var(--space-lg)">
      <div class="panel-title">Which shark, doing what</div>
      <div class="panel-sub">Recorded incidents by species (rows) and the victim's activity (columns). Darker = more. It shows, for example, wobbegongs biting divers and waders while white sharks strike swimmers and board-riders.</div>
      <div class="matrix-scroll" data-role="matrix"></div>
    </div>
  `;

  // Treemap
  const tmFrame = root.querySelector('[data-role="treemap"]') as HTMLElement;
  renderTreemap(tmFrame, named, ctx);
  window.addEventListener('resize', () => renderTreemap(tmFrame, named, ctx), { passive: true });

  // Fatal bars
  const fatalRows = named.filter((s) => s.fatal > 0).slice(0, 10);
  (root.querySelector('[data-role="fatal"]') as HTMLElement).appendChild(
    horizontalBars(
      fatalRows.map((s) => ({
        id: s.key,
        label: s.label,
        value: s.fatal,
        colour: OUTCOME_COLOUR.fatal,
        tip: `${s.label}: ${formatNumber(s.fatal)} fatal of ${formatNumber(s.total)} incidents (${formatPercent(s.fatalPct, 0)})`,
      })),
      { onClick: (id) => ctx.openSpecies(id), labelWidth: 130 },
    ),
  );

  // Fatal rate bars (>=10 incidents)
  const rateRows = named.filter((s) => s.total >= 10).sort((a, b) => b.fatalPct - a.fatalPct);
  (root.querySelector('[data-role="rate"]') as HTMLElement).appendChild(
    horizontalBars(
      rateRows.map((s) => ({
        id: s.key,
        label: s.label,
        value: s.fatalPct,
        colour: speciesInfo(s.key).colour,
        tip: `${s.label}: ${formatPercent(s.fatalPct, 0)} fatal (${formatNumber(s.fatal)} of ${formatNumber(s.total)})`,
      })),
      { onClick: (id) => ctx.openSpecies(id), labelWidth: 130, valueFormat: (v) => `${v.toFixed(0)}%` },
    ),
  );

  // Matrix
  renderMatrix(root.querySelector('[data-role="matrix"]') as HTMLElement, ctx);
}

function renderTreemap(frame: HTMLElement, rows: ReturnType<typeof speciesStats>, ctx: ViewContext): void {
  frame.innerHTML = '';
  const width = Math.max(320, frame.clientWidth || 900);
  const height = 360;
  const values = rows.map((r) => r.total);
  const rects = squarify(values, width, height);
  const svg = el('svg', { class: 'chart treemap', viewBox: `0 0 ${width} ${height}`, width, height, role: 'img' });
  rects.forEach((rect, i) => {
    const s = rows[i];
    const info = speciesInfo(s.key);
    const g = el('g', { style: 'cursor:pointer' });
    g.appendChild(
      el('rect', {
        x: rect.x + 1,
        y: rect.y + 1,
        width: Math.max(0, rect.w - 2),
        height: Math.max(0, rect.h - 2),
        rx: 3,
        fill: info.colour,
        'data-tip': `${s.label}: ${formatNumber(s.total)} incidents · ${formatNumber(s.fatal)} fatal (${formatPercent(s.fatalPct, 0)})`,
        'aria-label': `${s.label}: ${s.total} incidents`,
      }),
    );
    if (rect.w > 62 && rect.h > 30) {
      const t1 = el('text', { x: rect.x + 8, y: rect.y + 20, fill: '#fff', 'font-size': 13, 'font-weight': 700 });
      t1.textContent = s.label;
      g.appendChild(t1);
      const t2 = el('text', { x: rect.x + 8, y: rect.y + 37, fill: 'rgba(255,255,255,0.9)', 'font-size': 11, 'font-family': 'var(--font-mono)' });
      t2.textContent = `${formatNumber(s.total)}`;
      g.appendChild(t2);
    }
    g.addEventListener('click', () => ctx.openSpecies(s.key));
    svg.appendChild(g);
  });
  frame.appendChild(svg);
}

function renderMatrix(host: HTMLElement, ctx: ViewContext): void {
  const stats = speciesStats(ctx.data.incidents).filter((s) => s.key !== 'unidentified' && s.total >= 8);
  const speciesKeys = stats.map((s) => s.key);
  const activityKeys = ACTIVITY_ORDER.filter((a) => a !== 'other');
  const m = speciesActivityMatrix(ctx.data.incidents, speciesKeys, activityKeys);
  const max = Math.max(1, ...m.flat());

  const thead = `<thead><tr><th>Species ╲ Activity</th>${activityKeys.map((a) => `<th>${escapeHtml(activityLabel(a).replace(' / standing', ''))}</th>`).join('')}</tr></thead>`;
  const tbody = `<tbody>${speciesKeys
    .map((sk, r) => {
      const info = speciesInfo(sk);
      const cells = activityKeys
        .map((ak, cIdx) => {
          const v = m[r][cIdx];
          const t = v / max;
          const colour = v === 0 ? 'var(--bg-surface)' : heatColour(0.15 + t * 0.85);
          const fg = t > 0.45 ? '#fff' : 'var(--text-secondary)';
          const tip = escapeHtml(`${info.label} × ${activityLabel(ak)}: ${v} incident${v === 1 ? '' : 's'}`);
          return `<td style="background:${colour};color:${fg}" data-tip="${tip}" aria-label="${tip}">${v || ''}</td>`;
        })
        .join('');
      return `<tr><th class="matrix-row" data-open-species="${escapeHtml(sk)}" style="cursor:pointer;color:${info.colour}">${escapeHtml(info.label)}</th>${cells}</tr>`;
    })
    .join('')}</tbody>`;

  host.innerHTML = `<table class="matrix">${thead}${tbody}</table>`;
  host.querySelectorAll('[data-open-species]').forEach((b) =>
    b.addEventListener('click', () => ctx.openSpecies(b.getAttribute('data-open-species') as string)),
  );
}
